import { ball, body, coverBg, edge, fade, gloss, mix, n, rng, shadowInk, tint, type ArtPal } from './kit';

/** Sweet Shop: a glazed donut with sprinkles, a swirl lollipop and a macaron on a pastel counter. */
export function sweetShopCover(P: ArtPal, p: string): string {
  const top = P.dark ? mix(P.bg, P.pink, 0.14) : tint(P.pink, 0.86);
  const mid = P.dark ? mix(P.panel, P.purple, 0.1) : tint(P.purple, 0.9);
  const bottom = P.dark ? mix(P.bg, P.pink, 0.06) : tint(P.pink, 0.9);
  const bg = coverBg(p, P, top, mid, bottom, [450, 200]);
  const dough = P.dark ? mix(P.gold, P.orange, 0.35) : mix(P.gold, P.orange, 0.2);
  const counter = P.dark ? mix(P.panel, P.pink, 0.16) : '#FFFFFF';

  const dx = 440;
  const dy = 216;
  const R = 104;
  const hole = 32;

  // sprinkles on the glaze
  const r = rng(3);
  const cols = [P.teal, P.gold, P.blue, P.dark ? P.ink : P.purple, P.green, P.sky];
  const spr: string[] = [];
  for (let i = 0; i < 34; i++) {
    const a = r() * Math.PI * 2;
    const d = hole + 16 + r() * (R - hole - 34);
    const x = dx + Math.cos(a) * d;
    const y = dy + Math.sin(a) * d * 0.9 - 6;
    spr.push(`<rect x="${n(x - 6)}" y="${n(y - 2)}" width="12" height="4.2" rx="2.1" transform="rotate(${n(r() * 180)} ${n(x)} ${n(y)})" fill="${cols[i % cols.length]}"/>`);
  }

  // lollipop swirl (spiral)
  const lx = 590;
  const ly = 118;
  let sp = `M${lx} ${ly}`;
  for (let t = 0; t <= 4.5 * Math.PI; t += 0.3) {
    const rr = 2 + t * 2.4;
    sp += `L${n(lx + rr * Math.cos(t))} ${n(ly + rr * Math.sin(t))}`;
  }

  const defs =
    `<defs>${bg.defs}` +
    fade(`${p}sh`, shadowInk(P), P.dark ? 0.55 : 0.2) +
    body(`${p}ct`, counter, 0.3, P.dark ? 0.35 : 0.08, 0.3, 1) +
    ball(`${p}dg`, dough, 0.35, 0.4) +
    ball(`${p}gl`, P.pink, 0.45, 0.3) +
    ball(`${p}lp`, P.teal, 0.5, 0.35) +
    body(`${p}stk`, P.dark ? P.ink : tint(P.blue, 0.85), 0.4, 0.25, 1, 0.2) +
    body(`${p}mc`, P.green, 0.45, 0.3, 0.4, 1) +
    body(`${p}fl`, P.dark ? P.ink : tint(P.gold, 0.85), 0.4, 0.2, 0.4, 1) +
    `<mask id="${p}hole"><rect width="640" height="400" fill="#FFFFFF"/><ellipse cx="${dx}" cy="${dy - 4}" rx="${hole}" ry="${hole * 0.86}" fill="#000000"/></mask>` +
    `</defs>`;

  const glaze =
    `M${dx - R + 8} ${dy - 4}` +
    `C${dx - R + 4} ${dy - 80} ${dx - 40} ${dy - R + 4} ${dx} ${dy - R + 6}` +
    `C${dx + 50} ${dy - R + 4} ${dx + R - 4} ${dy - 70} ${dx + R - 8} ${dy - 6}` +
    `Q${dx + R - 2} ${dy + 18} ${dx + R - 22} ${dy + 26}Q${dx + 70} ${dy + 58} ${dx + 44} ${dy + 52}` +
    `Q${dx + 20} ${dy + 76} ${dx - 6} ${dy + 56}Q${dx - 40} ${dy + 70} ${dx - 56} ${dy + 46}` +
    `Q${dx - R + 4} ${dy + 46} ${dx - R + 8} ${dy - 4}Z`;

  const macaron =
    `<g transform="translate(596 312)">` +
    `<ellipse cx="0" cy="34" rx="50" ry="8" fill="url(#${p}sh)"/>` +
    `<path d="M-44 8H44Q46 32 0 34Q-46 32 -44 8Z" fill="url(#${p}mc)" stroke="${edge(P.green, P)}" stroke-width="2"/>` +
    `<rect x="-46" y="-4" width="92" height="14" rx="7" fill="url(#${p}fl)" stroke="${edge(P.gold, P)}" stroke-opacity=".4" stroke-width="1.5"/>` +
    `<path d="M-44 -4Q-44 -36 0 -36Q44 -36 44 -4Z" fill="url(#${p}mc)" stroke="${edge(P.green, P)}" stroke-width="2"/>` +
    gloss(-18, -24, 16, 5, -12, 0.5) +
    `</g>`;

  return (
    defs +
    bg.body +
    // counter
    `<path d="M150 400L190 320H640V400Z" fill="url(#${p}ct)"/>` +
    `<path d="M190 320H640" stroke="${P.dark ? mix(P.panel, P.pink, 0.3) : tint(P.pink, 0.6)}" stroke-width="3"/>` +
    // lollipop behind
    `<rect x="${lx - 4}" y="${ly + 40}" width="8" height="160" rx="4" fill="url(#${p}stk)" stroke="${edge(P.ink, P)}" stroke-opacity=".3" stroke-width="1.5"/>` +
    `<circle cx="${lx}" cy="${ly}" r="46" fill="url(#${p}lp)" stroke="${edge(P.teal, P)}" stroke-width="2"/>` +
    `<path d="${sp}" fill="none" stroke="#FFFFFF" stroke-opacity=".55" stroke-width="5" stroke-linecap="round" stroke-linejoin="round"/>` +
    gloss(lx - 18, ly - 20, 12, 6, -38, 0.6) +
    // donut
    `<ellipse cx="${dx + 12}" cy="${dy + R - 4}" rx="${R}" ry="16" fill="url(#${p}sh)"/>` +
    `<g mask="url(#${p}hole)">` +
    `<ellipse cx="${dx}" cy="${dy}" rx="${R}" ry="${R * 0.9}" fill="url(#${p}dg)" stroke="${edge(dough, P)}" stroke-width="2"/>` +
    `<path d="${glaze}" fill="url(#${p}gl)" stroke="${edge(P.pink, P)}" stroke-width="2" stroke-linejoin="round"/>` +
    spr.join('') +
    gloss(dx - 46, dy - 52, 30, 10, -30, 0.45) +
    `</g>` +
    `<ellipse cx="${dx}" cy="${dy - 4}" rx="${hole}" ry="${hole * 0.86}" fill="none" stroke="${edge(P.pink, P)}" stroke-width="2"/>` +
    macaron
  );
}
